import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { OrderStatusBadge } from '~/components/ui/OrderStatusBadge';
import { Button } from '~/components/ui';
import { sellerLabel, formatQuantityForUnit } from '~/utils';
import type { OrderStatus, UnitCode } from '~/graphql/graphql';
import { FulfilmentDecision } from '~/pages/order-view/sections/FulfilmentDecision';

const SHIPPED_STATUSES: OrderStatus[] = ['SHIPPED', 'DELIVERED'];

type SourcingQuantity = {
  value: number;
  unit: UnitCode;
};

type SourcingItem = {
  quantity: SourcingQuantity;
  platformProductsLengths: {
    id: string;
    lengthDisplayValue: string;
    platformProduct: {
      name: string;
    };
  };
};

type SourcingSubOrder = {
  id: string;
  status: OrderStatus;
  sellingOrganization: {
    name: string;
    sellingOrganizationCode?: string | null;
  };
  items: SourcingItem[];
};

export type SourcingOrder = {
  id: string;
  status: OrderStatus;
  isBrokered: boolean;
  shippingLocation?: { id: string } | null;
  items: SourcingItem[];
  subOrders: SourcingSubOrder[];
};

type CoverageRow = {
  platformProductsLengthId: string;
  productName: string;
  lengthDisplayValue: string;
  required: number;
  covered: number;
  unit: UnitCode;
};

function buildOrderBuilderSourcingUrl(order: SourcingOrder): string {
  const params = new URLSearchParams({
    parentOrderId: order.id,
    returnPath: `/orders/view/${order.id}`,
  });
  if (order.shippingLocation?.id) {
    params.set('dropShipLocationId', order.shippingLocation.id);
  }
  return `/order-builder/view?${params.toString()}`;
}

function summarizeItems(items: SourcingItem[]): string {
  return items
    .map(
      (item) =>
        `${item.platformProductsLengths.platformProduct.name} · ${item.platformProductsLengths.lengthDisplayValue} · ${formatQuantityForUnit(
          item.quantity
        )} ${item.quantity.unit}`
    )
    .join(', ');
}

/** Per parent line item: how much of it has been allocated across all sub-orders. */
function computeCoverage(order: SourcingOrder): CoverageRow[] {
  return order.items.map((item) => {
    const platformProductsLengthId = item.platformProductsLengths.id;
    const covered = order.subOrders
      .filter((subOrder) => subOrder.status !== 'CANCELLED')
      .flatMap((subOrder) => subOrder.items)
      .filter(
        (subItem) =>
          subItem.platformProductsLengths.id === platformProductsLengthId
      )
      .reduce((total, subItem) => total + subItem.quantity.value, 0);

    return {
      platformProductsLengthId,
      productName: item.platformProductsLengths.platformProduct.name,
      lengthDisplayValue: item.platformProductsLengths.lengthDisplayValue,
      required: item.quantity.value,
      covered,
      unit: item.quantity.unit,
    };
  });
}

/**
 * Replaces the seller "Ship Order" call to action for a trader on a
 * CONFIRMED order. Unbrokered (no sub-orders yet), it shows the
 * ship-vs-source decision. Brokered, it shows the sub-order roll-up,
 * allocation coverage guide, and sub-order list.
 */
export function SourcingPanel({
  order,
  onShipFromInventory,
}: {
  order: SourcingOrder;
  onShipFromInventory: () => void;
}) {
  const { t } = useTranslation('order');
  const navigate = useNavigate();

  const goToSourcingOrderBuilder = () =>
    navigate(buildOrderBuilderSourcingUrl(order));

  if (!order.isBrokered) {
    return (
      <FulfilmentDecision
        onShipFromInventory={onShipFromInventory}
        onSourceFromSellers={goToSourcingOrderBuilder}
      />
    );
  }

  const nonCancelledSubOrders = order.subOrders.filter(
    (subOrder) => subOrder.status !== 'CANCELLED'
  );
  const shippedCount = nonCancelledSubOrders.filter((subOrder) =>
    SHIPPED_STATUSES.includes(subOrder.status)
  ).length;
  const coverageRows = computeCoverage(order);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-neut-prim">
          {t('order.sourcing.panel_title')}
        </h3>
        <span className="text-sm text-gray-500">
          {t('order.sourcing.shipped_rollup', {
            shipped: shippedCount,
            total: nonCancelledSubOrders.length,
          })}
        </span>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-4">
        <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">
          {t('order.sourcing.coverage_title')}
        </div>
        <div className="flex flex-col gap-3">
          {coverageRows.map((row) => {
            const remaining = Math.max(row.required - row.covered, 0);
            const isFullyCovered = row.covered >= row.required;
            const progressPercent =
              row.required > 0
                ? Math.min(100, (row.covered / row.required) * 100)
                : 0;
            return (
              <div key={row.platformProductsLengthId}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-neut-prim flex items-center gap-2">
                    {row.productName}
                    <span className="px-2 py-1 bg-neut-100 rounded-md text-xs">
                      {row.lengthDisplayValue}
                    </span>
                  </span>
                  <span
                    className={
                      isFullyCovered
                        ? 'text-green-600 font-semibold'
                        : 'text-amber-600 font-semibold'
                    }
                  >
                    {isFullyCovered
                      ? t('order.sourcing.coverage_covered', {
                          covered: formatQuantityForUnit({
                            value: row.covered,
                            unit: row.unit,
                          }),
                          required: formatQuantityForUnit({
                            value: row.required,
                            unit: row.unit,
                          }),
                        })
                      : t('order.sourcing.coverage_partial', {
                          covered: formatQuantityForUnit({
                            value: row.covered,
                            unit: row.unit,
                          }),
                          required: formatQuantityForUnit({
                            value: row.required,
                            unit: row.unit,
                          }),
                          remaining: formatQuantityForUnit({
                            value: remaining,
                            unit: row.unit,
                          }),
                        })}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full">
                  <div
                    className={`h-1.5 rounded-full ${
                      isFullyCovered ? 'bg-green-600' : 'bg-amber-500'
                    }`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">
        {t('order.sourcing.sub_orders_title', {
          count: order.subOrders.length,
        })}
      </div>
      <div className="flex flex-col gap-2 mb-4">
        {order.subOrders.map((subOrder) => (
          <Link
            key={subOrder.id}
            to={`/orders/view/${subOrder.id}`}
            className="flex items-center justify-between bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-3 hover:bg-neut-hover cursor-pointer transition-colors"
          >
            <div>
              <div className="text-sm font-semibold text-neut-prim">
                {sellerLabel(subOrder.sellingOrganization)}
              </div>
              <div className="text-xs text-gray-500">
                {summarizeItems(subOrder.items)}
              </div>
            </div>
            <OrderStatusBadge status={subOrder.status} size="caption" />
          </Link>
        ))}
      </div>

      {order.status === 'CONFIRMED' && (
        <Button onClick={goToSourcingOrderBuilder}>
          {t('order.sourcing.add_sub_order')}
        </Button>
      )}
    </div>
  );
}
